import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { HouseholdInfo, HouseholdMember, HouseholdResponse } from '../types/Household';
import { authFetch } from '../lib/authFetch';

const HOUSEHOLD_NOT_FOUND = 'Household not found';

const readResponse = async (
  res: Response
): Promise<{ parsed: unknown; message: string }> => {
  const raw = await res.text();
  if (!raw) return { parsed: null, message: '' };

  try {
    const parsed = JSON.parse(raw) as { message?: string; error?: string };
    return {
      parsed,
      message: parsed.message?.trim() || parsed.error?.trim() || raw.trim(),
    };
  } catch {
    return { parsed: null, message: raw.trim() };
  }
};

export const useHousehold = () => {
  const { auth } = useAuth();
  const [household, setHousehold] = useState<HouseholdInfo | null>(null);
  const [members, setMembers] = useState<HouseholdMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEmpty, setIsEmpty] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const requestCounter = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    const url = '/api/household/me';

    if (!auth.token) {
      abortRef.current?.abort();
      setHousehold(null);
      setMembers([]);
      setIsEmpty(false);
      setLoading(false);
      setError('You must be signed in to load household.');
      return;
    }

    abortRef.current?.abort();
    const requestId = ++requestCounter.current;
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');
    setIsEmpty(false);

    try {
      const res = await authFetch(url, {
        headers: { Authorization: `Bearer ${auth.token}` },
        signal: controller.signal,
      });
      const { parsed, message } = await readResponse(res);

      if (requestId !== requestCounter.current) return;

      if (!res.ok) {
        if (res.status === 404 && message === HOUSEHOLD_NOT_FOUND) {
          setHousehold(null);
          setMembers([]);
          setIsEmpty(true);
          setError('');
          return;
        }
        throw new Error(message || `Request failed (${res.status})`);
      }

      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Invalid response payload');
      }

      const payload = parsed as HouseholdResponse;
      if (!payload.household || !Array.isArray(payload.members)) {
        throw new Error('Invalid response payload');
      }

      setHousehold(payload.household);
      setMembers(payload.members);
      setIsEmpty(false);
    } catch (err) {
      if (requestId !== requestCounter.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      setHousehold(null);
      setMembers([]);
      setIsEmpty(false);
      setError(err instanceof Error ? err.message : 'Unable to load household.');
    } finally {
      if (requestId === requestCounter.current) {
        setLoading(false);
      }
    }
  }, [auth.token]);

  useEffect(() => {
    load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load, reloadKey]);

  const reload = useCallback(() => {
    setReloadKey((prev) => prev + 1);
  }, []);

  return {
    household,
    members,
    loading,
    error,
    isEmpty,
    reload,
  };
};

export default useHousehold;
